import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Presentation } from './presentation';
import { fetchPreferenceConditions } from '../../../services/personProfiles';
import { scrollToElementById } from '../../../libs/scrollUtils';
import { PreferenceConditionCategory } from '../../../types/personShow';

type Props = {
  onChangedBody: (name: string, value: string[]) => void;
  preferenceConditions: string[];
  target: string;
};

export const Component: FunctionComponent<Props> = ({
  onChangedBody,
  preferenceConditions,
  target,
}) => {
  const [categories, setCategories] = useState<PreferenceConditionCategory[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    setSelectedConditions(Array.from(new Set(preferenceConditions)));
  }, [preferenceConditions]);

  useEffect(() => {
    let isActive = true;

    const request = async () => {
      try {
        const response = await fetchPreferenceConditions();
        if (isActive) setCategories(response.preferenceConditionCategories);
      } catch (error) {
        console.log(error);
      }
    };

    request();
    return () => {
      isActive = false;
    };
  }, []);

  const filteredCategories = useMemo(() => {
    const query = filterText.trim().toLocaleLowerCase();
    if (query === '') return categories;

    return categories
      .map(category => ({
        ...category,
        preferenceConditions: category.preferenceConditions.filter(condition =>
          condition.name.toLocaleLowerCase().includes(query)
        ),
      }))
      .filter(category => category.preferenceConditions.length > 0);
  }, [categories, filterText]);

  const handleClick = useCallback((name: string) => {
    const updated = selectedConditions.includes(name)
      ? selectedConditions.filter(condition => condition !== name)
      : [...selectedConditions, name];
    setSelectedConditions(updated);
    onChangedBody(target, updated);
  }, [onChangedBody, selectedConditions, target]);

  const handleScrollTo = useCallback((categoryId: number) => {
    scrollToElementById(`preference_condition_category_${categoryId}`);
  }, []);

  return (
    <Presentation
      categories={filteredCategories}
      selectedConditions={selectedConditions}
      filterText={filterText}
      onChangeFilter={setFilterText}
      onClick={handleClick}
      onScrollTo={handleScrollTo}
    />
  );
};
